<template>
  <van-popup :show="show " @update:show="$emit('update:show', $event)" :close-on-click-overlay="true"
    class="reward-dialog" round>
    <div class="reward-content" @click.stop>
      <!-- 顶部插图区域 -->
      <div class="reward-illustration">
        <!-- 首冲图片 -->
        <img :src="getAssetsFile('activity/zu8.png')" alt="Reward" class="reward-image">
      </div>

      <!-- 文字和按钮区域 -->
      <div class="reward-text color-[#6A6A6A]">
        <p class="reward-message color-[#6A6A6A]">
          {{ getLocalizedRemark(popupdata?.remark) }}
        </p>
        <div class="mb-[10px]"> <span class="reward-amount">{{ VITE_symbol }}{{ numberFormat(popupdata?.amount, {
          minDecimals: 2
        }) }}</span> </div>
        <!-- 确认按钮 -->
        <div class="flex items-center justify-center">
          <div class="reward-button" @click="handleConfirm">
            {{ t('rewardDialog.confirm') }}
          </div>
        </div>
      </div>

    </div>

    <!-- 关闭图标 - 放在弹窗外面，遮罩层上 -->
    <div class="close-icon-container" @click="handleClose">
      <div class="close-icon">
        <div class="close-x"></div>
      </div>
    </div>
  </van-popup>

  <!-- RewardDialog 弹窗 -->
  <RewardDialog :show="showRewardDialog" :amount="rewardAmount" @update:show="showRewardDialog = $event"
    @confirm="handleRewardConfirm" />

  <!-- Redouble 弹窗 -->
  <Redouble :show="showRedoubleDialog" :amount="redoubleAmount" @update:show="showRedoubleDialog = $event"
    @confirm="handleRedoubleConfirm" />
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { numberFormat } from '@/utils/formatter'
import { getPopup, drawRewards } from '@/api/game'
import RewardDialog from './RewardDialog.vue'
import Redouble from './redouble.vue'
import { getAssetsFile } from '@/utils/pub-use'
const VITE_symbol = import.meta.env.VITE_SYMBOL
const { t, locale } = useI18n()

interface Props {
  show: boolean
  amount: string | number
}

interface Emits {
  (e: 'update:show', value: boolean): void
  (e: 'confirm'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// 弹窗状态
const showRewardDialog = ref(false)
const showRedoubleDialog = ref(false)
const rewardAmount = ref<any>({})
const redoubleAmount = ref<any>({})

// 关闭按钮处理
const handleClose = () => {
  emit('update:show', false)
}

// 根据当前语言获取对应的文本
const getLocalizedRemark = (remarkData: any) => {
  if (!remarkData) return ''

  try {
    // 解析 JSON 字符串
    const parsedData = JSON.parse(remarkData)

    // 根据当前语言获取对应的文本
    const currentLocale = locale.value

    // 直接使用下标访问
    if (parsedData[currentLocale]) {
      return parsedData[currentLocale]
    }

    // 处理语言代码映射：如果当前是 en-US，尝试 pt
    if (currentLocale === 'en-US' && parsedData['en']) {
      return parsedData['en']
    }

    // 如果当前语言没有，尝试英语
    if (parsedData['en']) {
      return parsedData['en']
    }

    // 如果都没有，返回第一个可用的
    const keys = Object.keys(parsedData)
    if (keys.length > 0) {
      return parsedData[keys[0]]
    }

  } catch (error) {
    // 如果解析失败，直接返回原字符串
    return remarkData
  }

  return ''
}

const handleConfirm = () => {
  drawRewards().then(res => {
    console.log('领奖返回数据:', res)
    if (res.data) {
      // 打开 RewardDialog 弹窗，传递 reward（15）
      rewardAmount.value = res.data
      showRewardDialog.value = true

      // 如果有 amount、reward 或 rewardBound 值，保存用于后续打开 redouble 弹窗
      if (res.data.amount || res.data.reward || res.data.rewardBound > 0) {
        redoubleAmount.value = res.data  // 传递接口返回的完整数据（包含 amount 字段）
      }
    }
  })
  emit('update:show', false)
  emit('confirm')
}

// RewardDialog 确认后的处理
const handleRewardConfirm = () => {
  showRewardDialog.value = false

  // 如果有 amount、reward 或 rewardBound 值，延迟打开 redouble 弹窗
  if (redoubleAmount.value && (redoubleAmount.value.amount || redoubleAmount.value.reward || redoubleAmount.value.rewardBound > 0)) {
    setTimeout(() => {
      showRedoubleDialog.value = true
    }, 300) // 延迟300ms确保上一个弹窗完全关闭
  }
}

// Redouble 确认后的处理
const handleRedoubleConfirm = () => {
  showRedoubleDialog.value = false
  redoubleAmount.value = 0 // 重置
}

const popupdata = ref<any>({})

// 监听弹窗显示状态，当弹窗显示时获取数据
watch(() => props.show, (newShow) => {
  if (newShow && !popupdata.value.amount) {
    // 只有当弹窗显示且还没有数据时才获取
    getPopup().then(res => {
      console.log('获取首充弹窗数据:', res)
      popupdata.value = res.data
    }).catch(error => {
      console.error('获取首充弹窗数据失败:', error)
    })
  }
}, { immediate: true })
</script>

<style scoped>
.reward-dialog {
  width: 80%;
  max-width: 320px;
  background: transparent;
  border-radius: 16px;
  overflow: visible;
  /* 改为 visible 让关闭按钮可以显示在弹窗外面 */
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.reward-content {
  padding-bottom: 0;
  text-align: center;
  background: transparent;
  border-radius: 16px;
}

.reward-illustration {
  margin-bottom: 0;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px 16px 0 0;
  position: relative;
  overflow: hidden;
  background: transparent;
}

.reward-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  position: relative;
  z-index: 2;
  border-radius: 16px 16px 0 0;
}

.reward-text {
  margin-bottom: 0;
  margin-top: -20px;
  background: white;
  padding: 20px;
  border-radius: 0 0 16px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.reward-message {
  font-size: 16px;
  color: #6A6A6A;
  line-height: 1.4;
  margin: 0;
  margin-bottom: 20px;
}

.reward-amount {
  color: #FF4444;
  font-weight: bold;
  font-size: 18px;

}

.reward-button {
  background: linear-gradient(180deg, #3CC35F 0%, #3A9857 100%);
  color: white;
  padding: 10px 0;
  border-radius: 4px;
  font-size: 13px;
  font-weight: bold;
  cursor: pointer;
  width: 35%;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(19, 199, 105, 0.3);
}

.reward-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(19, 199, 105, 0.4);
}

.reward-button:active {
  transform: translateY(0);
}

/* 关闭图标样式 */
.close-icon-container {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 15px;
  z-index: 1000;
  cursor: pointer;
}

.close-icon {
  width: 36px;
  height: 36px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.close-x {
  width: 16px;
  height: 16px;
  position: relative;
}

.close-x::before,
.close-x::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 2px;
  height: 16px;
  background: white;
  border-radius: 1px;
}

.close-x::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.close-x::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.close-icon:hover {
  background: rgba(0, 0, 0, 0.9);
  transform: scale(1.1);
  border-color: rgba(255, 255, 255, 0.5);
}

.close-icon:active {
  transform: scale(0.95);
}
</style>
